import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import {
  captureOfflinePrivacyResetEpoch,
  offlinePrivacyResetEpochIsCurrent,
  offlinePrivacyResetIsCurrent,
} from './offline-privacy-reset.ts';

/**
 * A deliberately small, selective cache for API reads that are useful while
 * walking a property without a connection.
 *
 * This is not a second TanStack Query cache. Query data is still kept in
 * memory and refetched normally; this store is consulted only after `fetch`
 * itself reports a network failure. Keeping the boundary here matters:
 * authorization errors, validation errors and server failures must still be
 * shown to the user rather than being hidden behind an old answer.
 *
 * Entries are scoped to the signed-in user, company and working location.
 * They are also bounded by age, entry count and serialized bytes, so opening a
 * large customer list cannot quietly turn into an unbounded browser database.
 */

const DB_NAME = 'blasto-offline-api-cache';
const DB_VERSION = 1;
const STORE = 'responses';
const FENCE_STORAGE_KEY = 'blasto-offline-api-cache-fences-v1';
const INVALIDATION_STORAGE_KEY = 'blasto-offline-api-cache-invalidations-v1';
const INVALIDATION_CHANNEL = 'blasto-offline-api-cache-invalidations-v1';

const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_ENTRY_BYTES = 512 * 1024;
const MAX_TOTAL_BYTES = 8 * 1024 * 1024;
const MAX_ENTRIES = 200;

/** Calendar list views are useful near today, not as an offline archive. */
const SCHEDULE_RANGE_MS = 45 * 24 * 60 * 60 * 1000;
const SCHEDULE_PAST_GRACE_MS = 14 * 24 * 60 * 60 * 1000;
const SCHEDULE_FUTURE_HORIZON_MS = 60 * 24 * 60 * 60 * 1000;

export type CacheKind = 'customers' | 'invoices' | 'quotes' | 'templates' | 'schedule' | 'inspections';

const ALL_CACHE_KINDS: readonly CacheKind[] = [
  'customers',
  'invoices',
  'quotes',
  'templates',
  'schedule',
  'inspections',
];

/** A busy area cannot evict every copy of a smaller, operationally useful one. */
const MAX_ENTRIES_BY_KIND: Record<CacheKind, number> = {
  customers: 60,
  invoices: 45,
  quotes: 40,
  templates: 20,
  schedule: 35,
  inspections: 35,
};

export interface OfflineCacheScope {
  userId: string;
  companyId: string;
  locationId: string | null;
  /**
   * Stable description of the role, permissions and plan/access entitlements
   * that authorized the read. IDs alone are not enough: a downgrade or lock
   * leaves the same account, company and branch ids, but must not make an old
   * privileged response eligible as an offline fallback.
   */
  authorization: string;
}

/**
 * Captured when an API request starts. The generation prevents a response
 * belonging to a session that has just signed out or switched location from
 * being written into the next scope.
 */
export interface OfflineGetContext {
  readonly key: string;
  readonly path: string;
  readonly scope: string;
  readonly kind: CacheKind;
  readonly generation: number;
  /** Privacy-wipe epoch captured before the network request left. */
  readonly privacyResetEpoch: string;
  /** Invalidates responses that left before a related mutation succeeded. */
  readonly kindGeneration: number;
  /** Invalidates an exact response whose live replacement was not cacheable. */
  readonly keyGeneration: number;
  /** Monotonic request order used to ignore late fallback/live completions. */
  readonly requestSequence: number;
  /**
   * Whether this dispatch obtained one of the bounded ordering-journal slots.
   * An untracked request that falls back fails closed with a generic warning.
   */
  readonly tracksFallbackStatus: boolean;
  /**
   * The last same-origin mutation announcement visible when this GET began.
   *
   * Storage events and BroadcastChannel messages are delivered asynchronously.
   * Reading this tiny persisted epoch again before every cache read/write keeps
   * a response that finished first in another tab from being written back
   * after that tab's mutation cleanup already deleted the old cache row.
   */
  readonly crossTabEpoch: string | null;
}

/** Captured before a write leaves, so a later location switch is harmless. */
export interface OfflineMutationContext {
  readonly scope: string;
  readonly kinds: readonly CacheKind[];
}

export interface OfflineGetHit<T> {
  data: T;
  cachedAt: number;
}

export interface OfflineFallbackStatus {
  /** At least one exact cached response is still being shown without a live replacement. */
  readonly active: boolean;
  /** Oldest active response, so a future detailed notice can explain its age. */
  readonly oldestCachedAt: number | null;
}

interface OfflineFallbackDataToken {
  readonly key: string;
  readonly requestSequence: number;
  readonly cachedAt: number;
  readonly context: OfflineGetContext;
}

const offlineFallbackDataTokens = new WeakMap<object, readonly OfflineFallbackDataToken[]>();
const offlineFallbackDataOverflow = new WeakMap<object, OfflineFallbackDataToken>();
const offlineFallbackDataWithoutTokens = new WeakSet<object>();
const MAX_FALLBACK_SCAN_NODES = 4096;

function isOfflineCachePayload(value: unknown): value is object {
  return typeof value === 'object' && value !== null;
}

/**
 * Associates a saved JSON object/array with the exact request that supplied it.
 * Weak metadata cannot leak into forms, mutations or JSON serialization, and
 * disappears with TanStack's query data after its final observer/GC.
 */
export function markOfflineFallbackData<T>(data: T, context: OfflineGetContext, cachedAt: number): T {
  if (!isOfflineCachePayload(data)) return data;
  offlineFallbackDataWithoutTokens.delete(data);
  offlineFallbackDataOverflow.delete(data);
  offlineFallbackDataTokens.set(data, [
    {
      key: context.key,
      requestSequence: context.requestSequence,
      cachedAt,
      context,
    },
  ]);
  return data;
}

/**
 * Promote tokens from wrappers such as InfiniteData to the query-data root.
 * Query responses are immutable JSON values, so a completed negative scan can
 * also be memoized. A breadth-first walk reaches every page root before a
 * single large page body; node/token bounds keep unusual custom data cheap.
 */
export function promoteOfflineFallbackDataTokens(data: unknown): boolean {
  if (!isOfflineCachePayload(data)) return false;
  if (offlineFallbackDataTokens.has(data) || offlineFallbackDataOverflow.has(data)) return true;
  if (offlineFallbackDataWithoutTokens.has(data)) return false;

  const visited = new WeakSet<object>();
  const pending: object[] = [data];
  const tokens: OfflineFallbackDataToken[] = [];
  let overflow: OfflineFallbackDataToken | undefined;
  let next = 0;
  let scannedNodes = 0;
  let completed = true;

  while (next < pending.length) {
    const candidate = pending[next];
    next += 1;
    if (!candidate) continue;
    if (visited.has(candidate)) continue;
    visited.add(candidate);
    scannedNodes += 1;
    if (scannedNodes > MAX_FALLBACK_SCAN_NODES) {
      completed = false;
      break;
    }

    if (candidate !== data) {
      const candidateTokens = offlineFallbackDataTokens.get(candidate);
      const candidateOverflow = offlineFallbackDataOverflow.get(candidate);
      if (candidateTokens || candidateOverflow) {
        for (const token of candidateTokens ?? []) {
          if (tokens.length < MAX_ENTRIES) tokens.push(token);
          else overflow ??= token;
        }
        overflow ??= candidateOverflow;
        continue;
      }
      if (offlineFallbackDataWithoutTokens.has(candidate)) continue;
    }

    const values = Array.isArray(candidate)
      ? candidate
      : Object.keys(candidate).map((key) => (candidate as Record<string, unknown>)[key]);
    for (const value of values) {
      if (isOfflineCachePayload(value) && !visited.has(value)) pending.push(value);
    }
  }

  if (tokens.length > 0) offlineFallbackDataTokens.set(data, tokens);
  if (overflow) offlineFallbackDataOverflow.set(data, overflow);
  if (tokens.length > 0 || overflow) return true;
  if (completed) offlineFallbackDataWithoutTokens.add(data);
  return false;
}

/** Oldest still-valid saved response represented by this observed query data. */
export function offlineFallbackCachedAt(data: unknown): number | null {
  if (!promoteOfflineFallbackDataTokens(data) || !isOfflineCachePayload(data)) return null;
  const cachedAt = (offlineFallbackDataTokens.get(data) ?? [])
    .filter(
      (token) =>
        token.key === token.context.key &&
        token.requestSequence === token.context.requestSequence &&
        fallbackContextIsCurrent(token.context),
    )
    .map((token) => token.cachedAt);
  const overflow = offlineFallbackDataOverflow.get(data);
  if (overflow && fallbackContextIsCurrent(overflow.context)) cachedAt.push(overflow.cachedAt);
  return cachedAt.length === 0 ? null : Math.min(...cachedAt);
}

interface CachedGetResponse {
  key: string;
  path: string;
  scope: string;
  kind: CacheKind;
  data: unknown;
  cachedAt: number;
  lastAccessedAt: number;
  sizeBytes: number;
  /** Unique owner for conditional cleanup after a late asynchronous put. */
  writeId: string;
  /** Privacy epoch that is also part of the durable key. */
  privacyResetEpoch: string;
  /** The bounded global mutation epoch when this network answer was stored. */
  crossTabEpoch: string | null;
}

interface OfflineCacheSchema extends DBSchema {
  [STORE]: {
    key: string;
    value: CachedGetResponse;
    indexes: {
      'by-accessed': number;
      'by-scope': string;
    };
  };
}

let dbPromise: Promise<IDBPDatabase<OfflineCacheSchema>> | null = null;
let activeScope: string | null = null;
let scopeGeneration = 0;
let cacheWriteSequence = 0;

interface OfflineFallbackEntry {
  cachedAt: number;
  requestSequence: number;
}

interface OfflineRequestHistory {
  /** Newest terminal outcome while an older same-key request is still outstanding. */
  latestOutcomeSequence: number;
  /** Only requests that have not yet produced live, fallback or failure. */
  pending: Set<number>;
}

const offlineFallbacks = new Map<string, OfflineFallbackEntry>();
const offlineRequestHistory = new Map<string, OfflineRequestHistory>();
const offlineFallbackListeners = new Set<() => void>();
let trackedOfflineRequestCount = 0;
let offlineFallbackOverflow = false;
let offlineRequestSequence = 0;
let offlineFallbackStatus: OfflineFallbackStatus = {
  active: false,
  oldestCachedAt: null,
};

function notifyOfflineFallbackListeners(): void {
  for (const listener of offlineFallbackListeners) listener();
}

function publishOfflineFallbackStatus(): void {
  const oldestCachedAt =
    offlineFallbacks.size === 0 ? null : Math.min(...[...offlineFallbacks.values()].map((entry) => entry.cachedAt));
  const active = offlineFallbackOverflow || oldestCachedAt !== null;
  if (offlineFallbackStatus.active === active && offlineFallbackStatus.oldestCachedAt === oldestCachedAt) {
    return;
  }
  offlineFallbackStatus = { active, oldestCachedAt };
  notifyOfflineFallbackListeners();
}

function resetOfflineFallbackStatus(): void {
  if (
    offlineFallbacks.size === 0 &&
    offlineRequestHistory.size === 0 &&
    trackedOfflineRequestCount === 0 &&
    !offlineFallbackOverflow
  ) {
    return;
  }
  offlineFallbacks.clear();
  offlineRequestHistory.clear();
  trackedOfflineRequestCount = 0;
  offlineFallbackOverflow = false;
  publishOfflineFallbackStatus();
}

function trackOfflineRequest(key: string, requestSequence: number): boolean {
  // This is a concurrency bound, not a lifetime-key journal. A completed
  // request immediately releases its slot, so visiting 201 pages in sequence
  // cannot leave a false warning behind for the rest of the session.
  if (trackedOfflineRequestCount >= MAX_ENTRIES) return false;
  const history = offlineRequestHistory.get(key) ?? {
    latestOutcomeSequence: -1,
    pending: new Set<number>(),
  };
  history.pending.add(requestSequence);
  offlineRequestHistory.set(key, history);
  trackedOfflineRequestCount += 1;
  return true;
}

type OfflineRequestOutcome = 'fallback' | 'live' | 'failure';

function recordOfflineOutcome(
  context: OfflineGetContext | null,
  outcome: OfflineRequestOutcome,
  cachedAt?: number,
): void {
  if (!context) return;

  if (!context.tracksFallbackStatus) {
    // Ordering is unknowable only when more than the cache's full capacity of
    // requests are simultaneously outstanding. A real fallback in that state
    // is surfaced conservatively until the tenant/privacy boundary changes.
    if (outcome === 'fallback' && isCurrent(context)) {
      offlineFallbackOverflow = true;
      publishOfflineFallbackStatus();
    }
    return;
  }

  const history = offlineRequestHistory.get(context.key);
  if (!history || !history.pending.delete(context.requestSequence)) return;
  trackedOfflineRequestCount = Math.max(0, trackedOfflineRequestCount - 1);

  const current = isCurrent(context);
  // A failed dispatch did not replace anything. It releases its bounded slot,
  // but must not suppress an older request that really did return saved data.
  const hasReplacementOutcome = outcome !== 'failure';
  const applies = hasReplacementOutcome && current && context.requestSequence >= history.latestOutcomeSequence;
  if (hasReplacementOutcome && context.requestSequence > history.latestOutcomeSequence) {
    history.latestOutcomeSequence = context.requestSequence;
  }

  if (applies && outcome === 'fallback' && cachedAt !== undefined) {
    if (!offlineFallbacks.has(context.key) && offlineFallbacks.size >= MAX_ENTRIES) {
      offlineFallbackOverflow = true;
    } else {
      offlineFallbacks.set(context.key, {
        cachedAt,
        requestSequence: context.requestSequence,
      });
    }
  } else if (applies && outcome === 'live') {
    const active = offlineFallbacks.get(context.key);
    if (!active || active.requestSequence <= context.requestSequence) {
      offlineFallbacks.delete(context.key);
    }
  }

  if (history.pending.size === 0) offlineRequestHistory.delete(context.key);
  publishOfflineFallbackStatus();
}

export function subscribeOfflineFallbackStatus(listener: () => void): () => void {
  offlineFallbackListeners.add(listener);
  return () => offlineFallbackListeners.delete(listener);
}

export function getOfflineFallbackStatus(): OfflineFallbackStatus {
  return offlineFallbackStatus;
}

/*
 * Mutations and cache maintenance intentionally use two layers of state:
 *
 *  - generations synchronously make an old context ineligible; and
 *  - the promises remove the corresponding IndexedDB rows in the background.
 *
 * A fresh refetch captures the new generation. Its cache write waits for the
 * removal promise before putting anything, so a slow deletion cannot erase a
 * newer network answer. Normal GET responses never await this work; a
 * successful mutation awaits only its bounded deletion so the guarantee also
 * holds when synchronous browser storage is unavailable.
 */
const kindGenerations = new Map<string, number>();
const keyGenerations = new Map<string, number>();
const pendingKindCleanup = new Map<string, Promise<boolean>>();
const pendingKeyCleanup = new Map<string, Promise<boolean>>();

interface PersistedFences {
  kinds: string[];
  keys: string[];
}

interface CrossTabInvalidation {
  id: string;
  source: string;
  kindStateKeys: string[];
  keys: string[];
}

function loadPersistedFences(): PersistedFences {
  try {
    if (typeof localStorage === 'undefined') return { kinds: [], keys: [] };
    const raw = localStorage.getItem(FENCE_STORAGE_KEY);
    if (!raw) return { kinds: [], keys: [] };
    const parsed = JSON.parse(raw) as Partial<PersistedFences>;
    return {
      kinds: Array.isArray(parsed.kinds)
        ? parsed.kinds.filter((value): value is string => typeof value === 'string')
        : [],
      keys: Array.isArray(parsed.keys) ? parsed.keys.filter((value): value is string => typeof value === 'string') : [],
    };
  } catch {
    return { kinds: [], keys: [] };
  }
}

const loadedFences = loadPersistedFences();
const persistedKindFences = new Set(loadedFences.kinds);
const persistedKeyFences = new Set(loadedFences.keys);

const crossTabSource = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
let crossTabSequence = 0;
const seenCrossTabInvalidations = new Set<string>();
const seenCrossTabInvalidationOrder: string[] = [];
const MAX_SEEN_CROSS_TAB_INVALIDATIONS = 256;
let invalidationChannel: BroadcastChannel | null = null;

/**
 * The ledger contains no response data, only opaque scope/kind or exact-key
 * tombstones. localStorage is used specifically because this fence must land
 * synchronously before a successful mutation is handed back to application
 * code; the larger response data remains in bounded IndexedDB.
 */
function persistFences(): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (persistedKindFences.size === 0 && persistedKeyFences.size === 0) {
      localStorage.removeItem(FENCE_STORAGE_KEY);
      return;
    }
    localStorage.setItem(
      FENCE_STORAGE_KEY,
      JSON.stringify({ kinds: [...persistedKindFences], keys: [...persistedKeyFences] } satisfies PersistedFences),
    );
  } catch {
    // Storage can be disabled or full. The in-memory fence still protects this
    // process, and IndexedDB deletion remains best-effort.
  }
}

function rememberCrossTabInvalidation(id: string): boolean {
  if (seenCrossTabInvalidations.has(id)) return false;
  seenCrossTabInvalidations.add(id);
  seenCrossTabInvalidationOrder.push(id);
  if (seenCrossTabInvalidationOrder.length > MAX_SEEN_CROSS_TAB_INVALIDATIONS) {
    const expired = seenCrossTabInvalidationOrder.shift();
    if (expired) seenCrossTabInvalidations.delete(expired);
  }
  return true;
}

function parseCrossTabInvalidation(value: unknown): CrossTabInvalidation | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Partial<CrossTabInvalidation>;
  if (
    typeof candidate.id !== 'string' ||
    typeof candidate.source !== 'string' ||
    !Array.isArray(candidate.kindStateKeys) ||
    !candidate.kindStateKeys.every((key) => typeof key === 'string') ||
    !Array.isArray(candidate.keys) ||
    !candidate.keys.every((key) => typeof key === 'string')
  ) {
    return null;
  }
  return candidate as CrossTabInvalidation;
}

/**
 * Coalesces bursts without dropping work requested while a pass is running.
 *
 * A plain shared promise only says "there is a pass". It cannot say that a
 * write landed after that pass took its snapshot, which is precisely the
 * write that needs another pass. The requested bit drains until no caller has
 * arrived during the preceding task.
 *
 * Exported as an internal utility so the concurrency boundary can be tested
 * without requiring a browser IndexedDB implementation.
 */
export function createCoalescedAsyncRunner(task: () => Promise<void>): () => void {
  let active: Promise<void> | null = null;
  let requested = false;

  function request(): void {
    requested = true;
    if (active) return;

    const drain = async () => {
      while (requested) {
        requested = false;
        await task();
      }
    };
    active = drain().finally(() => {
      active = null;
      // Covers a request that arrived after the loop observed `false` but
      // before this finally callback released the active promise.
      if (requested) request();
    });
  }

  return request;
}

function database(): Promise<IDBPDatabase<OfflineCacheSchema>> {
  if (dbPromise) return dbPromise;

  const opening = openDB<OfflineCacheSchema>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      const store = db.createObjectStore(STORE, { keyPath: 'key' });
      store.createIndex('by-accessed', 'lastAccessedAt');
      store.createIndex('by-scope', 'scope');
    },
  });
  dbPromise = opening;
  // Private browsing modes and embedded browsers may refuse IndexedDB. Do
  // not permanently memoize that refusal; a later attempt may be allowed.
  void opening.catch(() => {
    if (dbPromise === opening) dbPromise = null;
  });
  return opening;
}

/** IndexedDB is an enhancement. Every operation fails soft when unavailable. */
async function safely<T>(operation: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await operation();
  } catch {
    return fallback;
  }
}

function scopeKey(scope: OfflineCacheScope): string {
  // An encoded tuple has no delimiter-collision cases when ids evolve.
  return JSON.stringify([scope.userId, scope.companyId, scope.locationId, scope.authorization]);
}

function kindStateKey(scope: string, kind: CacheKind): string {
  return JSON.stringify([scope, kind]);
}

function currentKindGeneration(scope: string, kind: CacheKind): number {
  return kindGenerations.get(kindStateKey(scope, kind)) ?? 0;
}

function currentKeyGeneration(key: string): number {
  return keyGenerations.get(key) ?? 0;
}

/**
 * A bounded, synchronous cross-tab write epoch.
 *
 * The announcement slot holds only the latest small message. Its unique id is
 * enough to conservatively fence every GET that began before any mutation;
 * the normal kind/key generations still keep same-tab invalidation precise.
 */
function currentCrossTabInvalidation(): CrossTabInvalidation | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(INVALIDATION_STORAGE_KEY);
    if (!raw) return null;
    return parseCrossTabInvalidation(JSON.parse(raw) as unknown);
  } catch {
    return null;
  }
}

function currentCrossTabEpoch(): string | null {
  return currentCrossTabInvalidation()?.id ?? null;
}

let observedCrossTabEpoch = currentCrossTabEpoch();

/**
 * Consume a same-origin announcement synchronously when a request starts.
 * This closes the interval after localStorage changed but before the browser
 * delivers its `storage`/BroadcastChannel task: a newly-started offline read
 * must not see the row the other tab is in the process of retiring.
 */
function synchronizeCrossTabInvalidation(): void {
  const message = currentCrossTabInvalidation();
  if (!message) {
    observedCrossTabEpoch = null;
    return;
  }
  if (message.id === observedCrossTabEpoch) return;
  observedCrossTabEpoch = message.id;
  applyCrossTabInvalidation(message);
}

/**
 * Changes which entries may be read and written. Passing null is synchronous:
 * in-flight requests are fenced out immediately even though disk cleanup is
 * necessarily asynchronous.
 */
export function setOfflineCacheScope(scope: OfflineCacheScope | null): void {
  // Remembering the intended scope is safe while a privacy reset is pending:
  // every capture/read/write remains blocked by the reset epoch. This also
  // prevents a login that finishes during the wipe from leaving offline
  // caching disabled after the wipe completes.
  const next = scope ? scopeKey(scope) : null;
  if (next === activeScope) return;
  resetOfflineFallbackStatus();
  activeScope = next;
  scopeGeneration += 1;
  // Query data can outlive the old scope until SessionProvider purges it.
  // Re-evaluate observer-scoped tokens synchronously at the privacy boundary.
  notifyOfflineFallbackListeners();
  if (next) resumePersistedCleanup(next);
}

function fallbackContextIsCurrent(context: OfflineGetContext): boolean {
  return (
    offlinePrivacyResetEpochIsCurrent(context.privacyResetEpoch) &&
    context.scope === activeScope &&
    context.generation === scopeGeneration
  );
}

function isCurrent(context: OfflineGetContext): boolean {
  return (
    fallbackContextIsCurrent(context) &&
    context.kindGeneration === currentKindGeneration(context.scope, context.kind) &&
    context.keyGeneration === currentKeyGeneration(context.key) &&
    context.crossTabEpoch === currentCrossTabEpoch()
  );
}

/**
 * Records the exact cached answer now visible to the app. Entries remain active
 * until that same key receives a newer valid live replacement; an unrelated
 * background success must not hide a stale invoice, schedule or customer page.
 */
export function recordOfflineFallback(context: OfflineGetContext, cachedAt: number): void {
  recordOfflineOutcome(context, 'fallback', cachedAt);
}

/** Clears only the exact current key that a valid live response replaced. */
export function recordOfflineLiveResponse(context: OfflineGetContext | null): void {
  recordOfflineOutcome(context, 'live');
}

/** Releases request-order bookkeeping without changing a visible fallback. */
export function recordOfflineRequestFailure(context: OfflineGetContext | null): void {
  recordOfflineOutcome(context, 'failure');
}

function cleanupIsPending(context: OfflineGetContext): boolean {
  const kindKey = kindStateKey(context.scope, context.kind);
  return (
    persistedKindFences.has(kindKey) ||
    persistedKeyFences.has(context.key) ||
    pendingKindCleanup.has(kindKey) ||
    pendingKeyCleanup.has(context.key)
  );
}

function parsedPath(path: string): URL | null {
  if (!path.startsWith('/')) return null;
  try {
    return new URL(path, 'https://offline.invalid');
  } catch {
    return null;
  }
}

function isSingleSegment(value: string, prefix: string): boolean {
  if (!value.startsWith(prefix)) return false;
  const remainder = value.slice(prefix.length);
  return remainder.length > 0 && !remainder.includes('/');
}

function scheduleListIsNearTerm(url: URL): boolean {
  const from = Date.parse(url.searchParams.get('from') ?? '');
  const to = Date.parse(url.searchParams.get('to') ?? '');
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from || to - from > SCHEDULE_RANGE_MS) return false;

  const now = Date.now();
  return to >= now - SCHEDULE_PAST_GRACE_MS && from <= now + SCHEDULE_FUTURE_HORIZON_MS;
}

/**
 * Returns the cache budget a path belongs to, or null for every other API.
 *
 * Matching is intentionally explicit. In particular, auth, payments,
 * billing, messages, files, reports, search and admin data never enter this
 * store. Query strings remain part of the key, so an offline result is always
 * the exact filter/page/range that succeeded online before.
 */
function cacheKind(path: string): CacheKind | null {
  const url = parsedPath(path);
  if (!url) return null;
  const pathname = url.pathname;

  if (pathname === '/customers/map') return null;
  if (pathname === '/customers') return 'customers';
  if (isSingleSegment(pathname, '/customers/') && pathname !== '/customers/check-duplicates') return 'customers';
  if (/^\/customers\/[^/]+\/(properties|quotes)$/.test(pathname)) return 'customers';
  if (isSingleSegment(pathname, '/properties/')) return 'customers';

  if (pathname === '/invoices' || pathname === '/invoices/monthly') return 'invoices';
  if (/^\/invoices\/monthly\/[^/]+$/.test(pathname)) return 'invoices';
  if (isSingleSegment(pathname, '/invoices/') && pathname !== '/invoices/send-batch') return 'invoices';

  if (pathname === '/quotes') return 'quotes';
  if (isSingleSegment(pathname, '/quotes/')) return 'quotes';

  if (pathname === '/room-templates' || pathname === '/service-templates') return 'templates';

  // The small active roster is what turns assignee ids into names on a saved
  // calendar and inspection. Other company/settings endpoints stay excluded.
  if (pathname === '/company/users') return 'schedule';
  if (pathname === '/schedule/appointments') return scheduleListIsNearTerm(url) ? 'schedule' : null;
  if (/^\/schedule\/appointments\/[^/]+(?:\/worksheet)?$/.test(pathname)) return 'schedule';
  if (pathname === '/schedule/series' || /^\/schedule\/series\/[^/]+$/.test(pathname)) return 'schedule';
  if (/^\/schedule\/customers\/[^/]+$/.test(pathname)) return 'schedule';

  if (pathname === '/inspections' || pathname === '/inspections/actions/open') return 'inspections';
  if (isSingleSegment(pathname, '/inspections/') && pathname !== '/inspections/actions') return 'inspections';

  return null;
}

/** Series writes that recount `scheduled_series_count` on linked quotes. */
function scheduleMutationAffectsQuoteSeriesCount(pathname: string, method: string): boolean {
  if (pathname === '/schedule/series') return true;
  if (/^\/schedule\/series\/[^/]+\/end$/.test(pathname)) return true;
  if (method === 'PATCH' && /^\/schedule\/series\/[^/]+$/.test(pathname)) return true;
  return false;
}

/**
 * Cache families whose representations can contain data changed by a write.
 *
 * The groups are deliberately broader than an exact URL. A quote mutation,
 * for example, also changes the quote lists nested under a customer; an
 * appointment completion can change invoice and inspection summaries. For an
 * unfamiliar write we retire every small cache family instead of risking a
 * plausible-looking pre-mutation answer.
 */
function mutationCacheKinds(path: string, method: string): readonly CacheKind[] {
  const pathname = parsedPath(path)?.pathname;
  if (!pathname) return ALL_CACHE_KINDS;

  if (pathname.startsWith('/customers') || pathname.startsWith('/properties')) {
    return ['customers', 'invoices', 'quotes', 'schedule', 'inspections'];
  }
  if (pathname.startsWith('/quotes') || /^\/magic-quotes\/[^/]+\/generate$/.test(pathname)) {
    return ['customers', 'invoices', 'quotes', 'schedule'];
  }
  if (pathname.startsWith('/invoices') || pathname.startsWith('/change-orders') || pathname.startsWith('/payments')) {
    return ['invoices', 'quotes', 'schedule'];
  }
  if (pathname.startsWith('/room-templates') || pathname.startsWith('/service-templates')) return ['templates'];
  if (pathname.startsWith('/schedule')) {
    const kinds: CacheKind[] = ['invoices', 'schedule', 'inspections'];
    // Only booking or ending a series recounts scheduled_series_count (the
    // Scheduled badge). Visit confirmations, checklists, and feed edits must
    // not wipe persisted quote GETs the list needs offline.
    if (scheduleMutationAffectsQuoteSeriesCount(pathname, method)) kinds.push('quotes');
    return kinds;
  }
  if (pathname.startsWith('/inspections')) return ['schedule', 'inspections'];
  if (pathname.startsWith('/company/users')) return ['schedule', 'inspections'];
  if (pathname.startsWith('/files/quotes/')) return ['quotes'];
  if (pathname.startsWith('/files/appointments/')) return ['schedule'];
  if (pathname.startsWith('/files/inspections/')) return ['inspections'];

  return ALL_CACHE_KINDS;
}

export function isOfflineCacheablePath(path: string): boolean {
  return cacheKind(path) !== null;
}

/** Capture scope once, before a request can race a sign-out or location switch. */
export function captureOfflineGetContext(path: string): OfflineGetContext | null {
  const privacyResetEpoch = captureOfflinePrivacyResetEpoch();
  if (!privacyResetEpoch) return null;
  synchronizeCrossTabInvalidation();
  const kind = cacheKind(path);
  if (!activeScope || !kind) return null;
  // The privacy epoch is part of the key, not just metadata. If an old tab's
  // put is queued after reset clear completes, it cannot overwrite a fresh
  // same-user/same-path row written after login.
  const key = JSON.stringify([activeScope, path, privacyResetEpoch]);
  const requestSequence = ++offlineRequestSequence;
  return {
    key,
    path,
    scope: activeScope,
    kind,
    generation: scopeGeneration,
    privacyResetEpoch,
    kindGeneration: currentKindGeneration(activeScope, kind),
    keyGeneration: currentKeyGeneration(key),
    requestSequence,
    tracksFallbackStatus: trackOfflineRequest(key, requestSequence),
    crossTabEpoch: currentCrossTabEpoch(),
  };
}

/** Capture the original tenant scope before a write can race navigation. */
export function offlineSessionIdentity(): string {
  // Read-only dispatch fence for proposals returned by POST. Include the
  // monotonic generation so leaving and returning to a scope stays stale.
  return JSON.stringify([activeScope, scopeGeneration, captureOfflinePrivacyResetEpoch()]);
}

export function captureOfflineMutationContext(path: string, method = 'POST'): OfflineMutationContext | null {
  if (!offlinePrivacyResetIsCurrent() || !activeScope) return null;
  return { scope: activeScope, kinds: mutationCacheKinds(path, method) };
}

/** Fetch rejects with TypeError for network failures; abort is never a cache hit. */
export function isOfflineNetworkFailure(error: unknown): boolean {
  if (typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'AbortError') {
    return false;
  }
  return error instanceof TypeError;
}

async function removeKinds(scope: string, kinds: readonly CacheKind[]): Promise<boolean> {
  const selected = new Set(kinds);
  return safely(async () => {
    const db = await database();
    const transaction = db.transaction(STORE, 'readwrite');
    const entries = await transaction.store.index('by-scope').getAll(scope);
    // Queue every bounded deletion in the same transaction at once. Awaiting
    // each row serially adds an IndexedDB event-loop turn per cached response
    // to the successful mutation path; Promise.all keeps the durable fence
    // while avoiding that avoidable latency.
    await Promise.all([
      ...entries.filter((entry) => selected.has(entry.kind)).map((entry) => transaction.store.delete(entry.key)),
      transaction.done,
    ]);
    return true;
  }, false);
}

async function removeExactKey(key: string): Promise<boolean> {
  return safely(async () => {
    const db = await database();
    await db.delete(STORE, key);
    return true;
  }, false);
}

function scheduleKindRemoval(scope: string, kinds: readonly CacheKind[]): Promise<boolean> {
  if (kinds.length === 0) return Promise.resolve(true);

  const stateKeys = kinds.map((kind) => kindStateKey(scope, kind));
  const preceding = [
    ...new Set(stateKeys.map((key) => pendingKindCleanup.get(key)).filter((value) => value !== undefined)),
  ];

  const cleanup = (async () => {
    // A failed earlier pass does not prevent this pass from retrying the same
    // rows. Successful or not, all passes are ordered ahead of fresh writes.
    await Promise.all(preceding);
    return removeKinds(scope, kinds);
  })();
  for (const key of stateKeys) pendingKindCleanup.set(key, cleanup);

  void cleanup.then((removed) => {
    let changed = false;
    for (const key of stateKeys) {
      if (pendingKindCleanup.get(key) !== cleanup) continue;
      pendingKindCleanup.delete(key);
      if (removed) changed = persistedKindFences.delete(key) || changed;
    }
    if (changed) persistFences();
  });
  return cleanup;
}

function scheduleKeyRemoval(key: string): Promise<boolean> {
  const preceding = pendingKeyCleanup.get(key);
  const cleanup = (async () => {
    if (preceding) await preceding;
    return removeExactKey(key);
  })();
  pendingKeyCleanup.set(key, cleanup);

  void cleanup.then((removed) => {
    if (pendingKeyCleanup.get(key) !== cleanup) return;
    pendingKeyCleanup.delete(key);
    if (removed && persistedKeyFences.delete(key)) persistFences();
  });
  return cleanup;
}

function parseKindStateKey(key: string): { scope: string; kind: CacheKind } | null {
  try {
    const tuple = JSON.parse(key) as unknown;
    if (!Array.isArray(tuple) || tuple.length !== 2 || typeof tuple[0] !== 'string') return null;
    if (!ALL_CACHE_KINDS.includes(tuple[1] as CacheKind)) return null;
    return { scope: tuple[0], kind: tuple[1] as CacheKind };
  } catch {
    return null;
  }
}

function keyBelongsToScope(key: string, scope: string): boolean {
  try {
    const tuple = JSON.parse(key) as unknown;
    return Array.isArray(tuple) && (tuple.length === 2 || tuple.length === 3) && tuple[0] === scope;
  } catch {
    return false;
  }
}

function responseKeyIsValid(key: string): boolean {
  try {
    const tuple = JSON.parse(key) as unknown;
    return (
      Array.isArray(tuple) &&
      (tuple.length === 2 || tuple.length === 3) &&
      typeof tuple[0] === 'string' &&
      typeof tuple[1] === 'string' &&
      (tuple.length === 2 || typeof tuple[2] === 'string')
    );
  } catch {
    return false;
  }
}

/**
 * Applies the synchronous half of a mutation fence received from another tab,
 * then repeats the bounded disk cleanup in this tab. Repeating deletion is
 * intentional: the sender may have deleted its snapshot just before this tab
 * wrote a pre-mutation response that was already in flight.
 */
function applyCrossTabInvalidation(message: CrossTabInvalidation): void {
  if (message.source === crossTabSource || !rememberCrossTabInvalidation(message.id)) return;

  const removalsByScope = new Map<string, Set<CacheKind>>();
  let fencesChanged = false;
  for (const stateKey of message.kindStateKeys) {
    const parsed = parseKindStateKey(stateKey);
    if (!parsed) continue;
    if (!persistedKindFences.has(stateKey)) {
      persistedKindFences.add(stateKey);
      fencesChanged = true;
    }
    kindGenerations.set(stateKey, (kindGenerations.get(stateKey) ?? 0) + 1);
    const kinds = removalsByScope.get(parsed.scope) ?? new Set<CacheKind>();
    kinds.add(parsed.kind);
    removalsByScope.set(parsed.scope, kinds);
  }

  const exactKeys: string[] = [];
  for (const key of message.keys) {
    if (!responseKeyIsValid(key)) continue;
    if (!persistedKeyFences.has(key)) {
      persistedKeyFences.add(key);
      fencesChanged = true;
    }
    keyGenerations.set(key, (keyGenerations.get(key) ?? 0) + 1);
    exactKeys.push(key);
  }

  if (fencesChanged) persistFences();
  notifyOfflineFallbackListeners();
  for (const [scope, kinds] of removalsByScope) void scheduleKindRemoval(scope, [...kinds]);
  for (const key of exactKeys) void scheduleKeyRemoval(key);
}

function announceCrossTabInvalidation(kindStateKeys: readonly string[], keys: readonly string[]): void {
  if (kindStateKeys.length === 0 && keys.length === 0) return;
  crossTabSequence += 1;
  const message: CrossTabInvalidation = {
    id: `${crossTabSource}:${crossTabSequence}`,
    source: crossTabSource,
    kindStateKeys: [...kindStateKeys],
    keys: [...keys],
  };

  try {
    invalidationChannel?.postMessage(message);
  } catch {
    // The storage event below is the compatibility path.
  }
  try {
    if (typeof localStorage === 'undefined') return;
    // The unique id makes every mutation a distinct storage event, even when a
    // preceding cleanup for the same kind has not finished yet.
    localStorage.setItem(INVALIDATION_STORAGE_KEY, JSON.stringify(message));
    observedCrossTabEpoch = message.id;
  } catch {
    // IndexedDB cleanup still protects this tab. Some embedded browsers disable
    // all synchronous storage, in which case BroadcastChannel remains best effort.
  }
}

if (typeof window !== 'undefined' && typeof window.BroadcastChannel !== 'undefined') {
  try {
    invalidationChannel = new window.BroadcastChannel(INVALIDATION_CHANNEL);
    invalidationChannel.addEventListener('message', (event: MessageEvent<unknown>) => {
      const message = parseCrossTabInvalidation(event.data);
      if (message) {
        observedCrossTabEpoch = message.id;
        applyCrossTabInvalidation(message);
      }
    });
  } catch {
    invalidationChannel = null;
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== INVALIDATION_STORAGE_KEY || !event.newValue) return;
    try {
      const message = parseCrossTabInvalidation(JSON.parse(event.newValue) as unknown);
      if (message) {
        observedCrossTabEpoch = message.id;
        applyCrossTabInvalidation(message);
      }
    } catch {
      // Ignore malformed same-origin storage messages.
    }
  });
}

/** Resume tombstone cleanup after a reload without ever exposing the row. */
function resumePersistedCleanup(scope: string): void {
  const kinds: CacheKind[] = [];
  for (const stateKey of persistedKindFences) {
    const parsed = parseKindStateKey(stateKey);
    if (parsed?.scope === scope && !pendingKindCleanup.has(stateKey)) kinds.push(parsed.kind);
  }
  if (kinds.length > 0) void scheduleKindRemoval(scope, kinds);

  for (const key of persistedKeyFences) {
    if (keyBelongsToScope(key, scope) && !pendingKeyCleanup.has(key)) void scheduleKeyRemoval(key);
  }
}

/** Fence one exact fallback immediately and remove its row best-effort. */
export async function retireOfflineGet(context: OfflineGetContext | null): Promise<void> {
  if (!context || !isCurrent(context)) return;

  persistedKeyFences.add(context.key);
  persistFences();
  keyGenerations.set(context.key, currentKeyGeneration(context.key) + 1);
  notifyOfflineFallbackListeners();
  announceCrossTabInvalidation([], [context.key]);
  await scheduleKeyRemoval(context.key);
}

/**
 * Fence related cached reads immediately after a server write commits. The
 * returned completion never rejects and covers the bounded disk deletion;
 * awaiting it makes the fence restart-safe even when localStorage is disabled.
 * Fresh refetch writes wait behind the same cleanup and cannot be erased by it.
 */
export async function invalidateOfflineGetsAfterMutation(context: OfflineMutationContext | null): Promise<void> {
  if (!context) return;
  const stateKeys = context.kinds.map((kind) => kindStateKey(context.scope, kind));
  for (const key of stateKeys) persistedKindFences.add(key);
  persistFences();

  // The generation change is the synchronous in-process fence. The persisted
  // tombstone above makes the same decision survive a tab/process restart.
  for (const key of stateKeys) kindGenerations.set(key, (kindGenerations.get(key) ?? 0) + 1);
  notifyOfflineFallbackListeners();
  announceCrossTabInvalidation(stateKeys, []);
  await scheduleKindRemoval(context.scope, context.kinds);
}

function ensureContextCleanup(context: OfflineGetContext): void {
  const kindKey = kindStateKey(context.scope, context.kind);
  if (persistedKindFences.has(kindKey) && !pendingKindCleanup.has(kindKey)) {
    void scheduleKindRemoval(context.scope, [context.kind]);
  }
  if (persistedKeyFences.has(context.key) && !pendingKeyCleanup.has(context.key)) void scheduleKeyRemoval(context.key);
}

async function waitForCleanup(context: OfflineGetContext): Promise<boolean> {
  ensureContextCleanup(context);
  const kindKey = kindStateKey(context.scope, context.kind);

  const tasks = [pendingKindCleanup.get(kindKey), pendingKeyCleanup.get(context.key)].filter(
    (value) => value !== undefined,
  );
  if (tasks.length === 0) return true;
  const results = await Promise.all(tasks);
  return results.every(Boolean) && !persistedKindFences.has(kindKey) && !persistedKeyFences.has(context.key);
}

export async function readOfflineGet<T>(context: OfflineGetContext): Promise<OfflineGetHit<T> | null> {
  ensureContextCleanup(context);
  if (!isCurrent(context) || cleanupIsPending(context)) return null;

  return safely(async () => {
    const db = await database();
    if (!isCurrent(context)) return null;

    // A read-write transaction updates LRU position without racing a newer
    // network response and accidentally putting an older value back over it.
    const transaction = db.transaction(STORE, 'readwrite');
    const entry = await transaction.store.get(context.key);
    if (!entry || !isCurrent(context)) {
      await transaction.done;
      return null;
    }

    // Rows from before reset epochs existed, and any pre-reset put that
    // happened to land after the clear transaction, are never eligible in the
    // current epoch. The key includes this value too, protecting a newer row
    // from being overwritten by that stale put.
    if (entry.privacyResetEpoch !== context.privacyResetEpoch) {
      await transaction.store.delete(context.key);
      await transaction.done;
      return null;
    }

    // A tab can miss a targeted announcement if a concurrent mutation
    // overwrites the single bounded localStorage slot before its event runs.
    // The global epoch on the row is the conservative backstop: after any
    // mutation, no older fallback is plausible enough to show offline. Rows
    // from releases before this field existed belong to the initial null epoch.
    if ((entry.crossTabEpoch ?? null) !== context.crossTabEpoch) {
      await transaction.store.delete(context.key);
      await transaction.done;
      return null;
    }

    // Every selectively cached endpoint returns a JSON object or array. A
    // primitive row from an older build cannot carry observer-scoped fallback
    // metadata, so discard it instead of displaying data without a warning.
    if (!isOfflineCachePayload(entry.data)) {
      await transaction.store.delete(context.key);
      await transaction.done;
      return null;
    }

    if (Date.now() - entry.cachedAt > MAX_AGE_MS) {
      await transaction.store.delete(context.key);
      await transaction.done;
      return null;
    }

    entry.lastAccessedAt = Date.now();
    await transaction.store.put(entry);
    await transaction.done;

    // Check once more after asynchronous work, so a sign-out that happened
    // while IndexedDB was busy cannot reveal the just-read value.
    return isCurrent(context) ? { data: entry.data as T, cachedAt: entry.cachedAt } : null;
  }, null);
}

function serializedSize(value: unknown): number | null {
  try {
    const serialized = JSON.stringify(value);
    if (serialized === undefined) return null;
    return new TextEncoder().encode(serialized).byteLength;
  } catch {
    return null;
  }
}

export async function writeOfflineGet<T>(context: OfflineGetContext, data: T): Promise<void> {
  if (!isCurrent(context)) return;
  // The operational endpoints in cacheKind all return objects/arrays. Refuse a
  // primitive rather than create a fallback that cannot be tied to a visible
  // query consumer, and retire any older exact object row at the same key.
  if (!isOfflineCachePayload(data)) {
    await retireOfflineGet(context);
    return;
  }
  const sizeBytes = serializedSize(data);
  if (sizeBytes === null || sizeBytes > MAX_ENTRY_BYTES) {
    // A successful live answer proves the older exact fallback is no longer a
    // faithful replacement. Fence it before returning to the caller, then
    // remove it asynchronously just like mutation invalidation.
    await retireOfflineGet(context);
    return;
  }

  // A refetch can finish while mutation cleanup is still deleting its cache
  // family. Waiting here is off the API response path and prevents the delete
  // from racing and erasing this newer answer.
  if (!(await waitForCleanup(context)) || !isCurrent(context)) return;

  const wrote = await safely(async () => {
    const db = await database();
    if (!isCurrent(context)) return false;
    const now = Date.now();
    cacheWriteSequence += 1;
    const writeId = crossTabSource + ':cache:' + cacheWriteSequence;
    await db.put(STORE, {
      key: context.key,
      path: context.path,
      scope: context.scope,
      kind: context.kind,
      data,
      cachedAt: now,
      lastAccessedAt: now,
      sizeBytes,
      writeId,
      privacyResetEpoch: context.privacyResetEpoch,
      crossTabEpoch: context.crossTabEpoch,
    });

    // A different tab can finish its clear transaction between the final check
    // above and this tab queueing the put. Remove only this exact stale write;
    // a newer response may already own the same key when cleanup runs.
    if (!isCurrent(context)) {
      const cleanup = db.transaction(STORE, 'readwrite');
      const stored = await cleanup.store.get(context.key);
      if (stored?.writeId === writeId) await cleanup.store.delete(context.key);
      await cleanup.done;
      return false;
    }
    return true;
  }, false);
  if (!wrote) return;

  // The API client does not await cache writes, so pruning is likewise kept
  // off the request's critical path. Coalesce bursts such as template paging.
  requestOfflinePrune();
}

export async function pruneOfflineGetCache(): Promise<void> {
  await safely(async () => {
    const db = await database();
    const entries = await db.getAll(STORE);
    const remove = new Set<string>();
    const cutoff = Date.now() - MAX_AGE_MS;

    for (const entry of entries) {
      if (entry.cachedAt < cutoff) remove.add(entry.key);
    }

    for (const kind of Object.keys(MAX_ENTRIES_BY_KIND) as CacheKind[]) {
      const inKind = entries
        .filter((entry) => entry.kind === kind && !remove.has(entry.key))
        .sort((a, b) => b.lastAccessedAt - a.lastAccessedAt);
      for (const entry of inKind.slice(MAX_ENTRIES_BY_KIND[kind])) remove.add(entry.key);
    }

    const remaining = entries
      .filter((entry) => !remove.has(entry.key))
      .sort((a, b) => a.lastAccessedAt - b.lastAccessedAt);
    let totalBytes = remaining.reduce((total, entry) => total + entry.sizeBytes, 0);
    let totalEntries = remaining.length;

    for (const entry of remaining) {
      if (totalEntries <= MAX_ENTRIES && totalBytes <= MAX_TOTAL_BYTES) break;
      remove.add(entry.key);
      totalEntries -= 1;
      totalBytes -= entry.sizeBytes;
    }

    if (remove.size === 0) return;
    const observedWriteIds = new Map(entries.map((entry) => [entry.key, entry.writeId]));
    const transaction = db.transaction(STORE, 'readwrite');
    for (const key of remove) {
      const current = await transaction.store.get(key);
      if (current && current.writeId === observedWriteIds.get(key)) {
        await transaction.store.delete(key);
      }
    }
    await transaction.done;
  }, undefined);
}

const requestOfflinePrune = createCoalescedAsyncRunner(pruneOfflineGetCache);

/**
 * Ends the active scope immediately and removes every user's offline API data.
 * Clearing the whole small store is both cheaper and safer than retaining old
 * accounts' data on a shared device.
 */
export async function clearOfflineGetCache(): Promise<boolean> {
  setOfflineCacheScope(null);
  const cleared = await safely(async () => {
    const db = await database();
    await db.clear(STORE);
    return true;
  }, false);
  if (!cleared) return false;

  kindGenerations.clear();
  keyGenerations.clear();
  pendingKindCleanup.clear();
  pendingKeyCleanup.clear();
  persistedKindFences.clear();
  persistedKeyFences.clear();
  persistFences();
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(INVALIDATION_STORAGE_KEY);
  } catch {
    // Storage may be unavailable; all in-memory state is already fenced.
  }
  return true;
}
